import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Alert, AlertDescription } from "./ui/alert";
import { Check, ArrowUpCircle } from "lucide-react";
import { getPricingPlans, createSubscription, upgradeSubscription } from "../services/api";
import { OrganizationContext } from '../contexts/OrganizationContext';

const PricingSection = ({ onPlanSelected, hideNavigation = false }) => {
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscriptionAction, setSubscriptionAction] = useState('add');
  const [currentSubscriptionId, setCurrentSubscriptionId] = useState(null);

  const { organizationId } = useContext(OrganizationContext);
  const navigate = useNavigate();

  // Read localStorage once on mount — never triggers re-fetch
  useEffect(() => {
    const action = localStorage.getItem('subscriptionAction') || 'add';
    const subId  = localStorage.getItem('currentSubscriptionId');
    setSubscriptionAction(action);
    if (subId) setCurrentSubscriptionId(parseInt(subId));
  }, []);

  // Fetch plans once on mount — billingPeriod toggle must NOT re-fetch
  useEffect(() => {
    const fetchPricingPlans = async () => {
      try {
        setLoading(true);
        const data = await getPricingPlans();

        // Deduplicate features per plan (guard against duplicate DB rows)
        const dedupedPlans = (Array.isArray(data) ? data : []).map(plan => ({
          ...plan,
          features: Array.isArray(plan.features)
            ? [...new Set(plan.features)]   // Set removes exact duplicates
            : [],
        }));

        setPlans(dedupedPlans);
      } catch (err) {
        setError('Failed to load pricing plans. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPricingPlans();
  }, []); // ← empty array: fetch once, never re-fetch on toggle

  const handleGetStarted = async (planType) => {
    try {
      setIsProcessing(true);
      setError('');

      const selectedPlan = plans.find(plan => plan.planType === planType);
      if (!selectedPlan) throw new Error('Selected plan configuration not found');

      const planDetails = {
        type:                planType,
        billing:             billingPeriod,
        price:               billingPeriod === 'monthly' ? selectedPlan.monthlyPrice : selectedPlan.yearlyPrice,
        subscriptionPlan:    planType.toUpperCase(),
        subscriptionFreq:    billingPeriod === 'monthly' ? 'MONTHLY' : 'YEARLY',
        subscriptionAmount:  billingPeriod === 'monthly' ? selectedPlan.monthlyPrice : selectedPlan.yearlyPrice,
        coveredCallAmount:   selectedPlan.coveredCallAmount || 0,
        priceId:             billingPeriod === 'monthly' ? selectedPlan.monthlyPriceId : selectedPlan.yearlyPriceId,
        description:         `${planType} Plan - ${selectedPlan.minutesPerMonth} minutes/month`,
        productName:         `${planType.charAt(0).toUpperCase() + planType.slice(1)} Plan`,
        isActive:            subscriptionAction === 'add',
        startDate:           new Date().toISOString().split('T')[0],
      };

      if (subscriptionAction === 'upgrade' && organizationId) {
        const response = await upgradeSubscription(organizationId, planDetails);
        localStorage.removeItem('subscriptionAction');
        localStorage.removeItem('currentSubscriptionId');
        alert(`Subscription upgrade scheduled! Your new ${planType} plan will start on ${response.startDate}`);
        navigate('/subscriptions');

      } else if (subscriptionAction === 'add' && organizationId) {
        await createSubscription(organizationId, planDetails);
        localStorage.removeItem('subscriptionAction');
        navigate('/checkout');

      } else {
        if (hideNavigation && onPlanSelected) {
          onPlanSelected(planDetails);
        } else {
          localStorage.setItem('selectedPlan', JSON.stringify(planDetails));
          navigate('/register');
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to process your request. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading pricing plans...</div>;
  }

  return (
    <div className="container max-w-6xl mx-auto py-12 px-4">
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold mb-4">
          {subscriptionAction === 'upgrade' ? 'Upgrade Your Plan' : 'Choose Your Plan'}
        </h2>
        <p className="text-muted-foreground mb-8">
          {subscriptionAction === 'upgrade'
            ? 'Select your new plan. Your current subscription will end at the billing period, and the new plan will start immediately after.'
            : "Whether you're just getting started or scaling up, we've got you covered."}
        </p>

        <div className="flex items-center justify-center gap-4">
          <Label htmlFor="billing-toggle">Monthly</Label>
          <Switch
            id="billing-toggle"
            checked={billingPeriod === 'yearly'}
            onCheckedChange={(checked) => setBillingPeriod(checked ? 'yearly' : 'monthly')}
          />
          <div className="flex items-center gap-2">
            <Label htmlFor="billing-toggle">Yearly</Label>
            <Badge variant="secondary">Save 15%</Badge>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {plans.map((plan) => (
          // Key uses only plan.id — stable across billing period toggles
          // so React updates the card in place rather than remounting it
          <PricingCard
            key={plan.id}
            plan={plan}
            billingPeriod={billingPeriod}
            isProcessing={isProcessing}
            isUpgradeAction={subscriptionAction === 'upgrade'}
            onSelect={handleGetStarted}
          />
        ))}
      </div>

      <div className="mt-12 text-center text-sm text-muted-foreground">
        <p>All plans include 24/7 support and a 30-day money-back guarantee.</p>
        <p className="mt-2">
          Need a custom plan? <a href="/contact" className="p-0">Contact us</a>
        </p>
      </div>
    </div>
  );
};

// Extracted as a named component outside PricingSection so it is never
// redefined on each render (which was causing React to treat it as a
// new component type and fully remount it on every state change)
const PricingCard = ({ plan, billingPeriod, isProcessing, isUpgradeAction, onSelect }) => {
  const price    = billingPeriod === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
  const features = Array.isArray(plan.features) ? plan.features : [];

  return (
    <Card className={`relative ${plan.isPopular ? 'border-primary shadow-lg' : ''}`}>
      {plan.isPopular && (
        <Badge className="absolute top-4 right-4">Most Popular</Badge>
      )}

      <CardHeader>
        <CardTitle className="capitalize flex items-center gap-2">
          {plan.planType} Plan
          {isUpgradeAction && <ArrowUpCircle className="w-5 h-5 text-primary" />}
        </CardTitle>
        <CardDescription>
          {plan.planType === 'basic'   ? 'Perfect to get started'        :
           plan.planType === 'premium' ? 'Best for growing businesses'   :
                                        'For large enterprises'}
        </CardDescription>
        <div className="mt-4">
          <span className="text-3xl font-bold">£{price}</span>
          <span className="text-muted-foreground ml-1">
            /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
          </span>
        </div>
      </CardHeader>

      <CardContent>
        {features.length > 0 ? (
          <ul className="space-y-3">
            {features.map((feature, index) => (
              <li key={index} className="flex items-center">
                <Check className="w-4 h-4 text-primary mr-2 shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground italic">No features listed.</p>
        )}

        <Button
          className="w-full mt-6"
          variant={plan.isPopular ? "default" : "outline"}
          onClick={() => onSelect(plan.planType)}
          disabled={isProcessing}
        >
          {isProcessing ? 'Processing...' :
           isUpgradeAction ? `Upgrade to ${plan.planType}` :
           'Get Started'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default PricingSection;